import React, { ReactNode, MouseEvent, ChangeEvent, FocusEvent, FormEvent, KeyboardEvent, useEffect, useRef } from 'react';
import styles from './InputField.module.css';
import { classNames } from '../../utils';
import { InputSize } from './InputSize';
import { FieldState } from '../../molecules/form/Form';

interface BaseInputFieldProps {
    readonly className?: string;
    readonly id?: string;
    readonly name?: string;
    readonly placeholder?: string;
    readonly value: string;
    readonly type?: string;
    readonly inputMode?: 'none' | 'text' | 'tel' | 'url' | 'email' | 'numeric' | 'decimal' | 'search';
    readonly size?: InputSize;
    readonly iconBefore?: ReactNode;
    readonly iconAfter?: ReactNode;
    readonly required?: boolean;
    readonly maxlength?: number;
    readonly minlength?: number;
    readonly max?: string;
    readonly min?: string;
    readonly pattern?: string;
    disabled?: boolean;
    autofocus?: boolean;
    readonly onClick?: (e: MouseEvent) => void;
    readonly onFocus?: (e: FocusEvent) => void;
    readonly onBlur?: (e: FocusEvent) => void;
    readonly onInput?: (e: FormEvent) => void;
    readonly onKeyDown?: (e: KeyboardEvent) => void;
}

interface DefaultInputFieldProps extends BaseInputFieldProps {
    readonly isSearch?: false;
    readonly onChange?: (state: FieldState) => void;
}

interface SearchInputFieldProps extends BaseInputFieldProps {
    readonly isSearch: true;
    readonly onChange: (state: FieldState, matchSearch?: (str1: string, str2: string) => void) => void;
}

type InputFieldProps = DefaultInputFieldProps | SearchInputFieldProps;

const InputField = ({
    className,
    id,
    name,
    placeholder,
    value = '',
    type = 'text',
    inputMode,
    maxlength,
    minlength,
    max,
    min,
    pattern,
    size = 'lg',
    iconBefore,
    iconAfter,
    disabled,
    required,
    isSearch,
    autofocus = false,
    onClick,
    onChange,
    onFocus,
    onBlur,
    onInput,
    onKeyDown,
    ...rest
}: InputFieldProps) => {
    const input = useRef<HTMLInputElement>(null);

    const dataAttributes = Object.keys(rest)
        .filter((key) => key.startsWith('data-'))
        .reduce((acc, key) => {
            acc[key] = rest[key as keyof typeof rest];
            return acc;
        }, {} as Record<string, string>);

    useEffect(() => {
        if (input.current && autofocus) {
            input.current.focus();
        }
    }, [input])

    const handleClick = (e: MouseEvent) => {
        e.stopPropagation();
        onClick?.(e);
    }

    const handleInputClick = (e: MouseEvent) => {
        e.stopPropagation();
        onClick?.(e);
    }

    const matchSearch = (str1: string , str2: string) => {
        const normalizedString1 = str1
            .replaceAll(' ', '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        const normalizedString2 = str2
            .replaceAll(' ', '')
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')

        return normalizedString1.includes(normalizedString2);
    }

    const createFieldState = (target: HTMLInputElement): FieldState => {
        return {
            name: name,
            value: target.value,
        };
    }

    const handleChange = (e: ChangeEvent) => {
        if (!onChange) {
            return;
        }

        if (isSearch === true) {
            onChange(createFieldState(e.target as HTMLInputElement), matchSearch);
        } else {
            onChange(createFieldState(e.target as HTMLInputElement));
        }
    }

    const handleWheel = (e: MouseEvent) => {
        const target = e.target as HTMLInputElement;

        target.blur();

        e.stopPropagation();

        setTimeout(() => {
            target.focus();
        }, 0);
    }

    const handleKeyDown = (e: KeyboardEvent) => {
        if (type === 'number') {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                e.preventDefault();
            }
        }

        onKeyDown?.(e);
    }

    const getInputMode = () => {
        if (inputMode !== undefined) {
            return inputMode;
        }

        if (type === 'number') {
            if (!!dataAttributes['data-no-decimals'] === true) {
                return 'numeric';
            }
            return 'decimal';
        }

        switch (type) {
            case 'email':
                return 'email';
            case 'tel':
                return 'tel';
            case 'search':
                return 'search';
            default:
                return undefined;
        }
    }

    return (
        <>
            <div
                onClick={handleClick}
                className={classNames(styles.InputField, iconBefore && styles.hasIconBefore, iconAfter && styles.hasIconAfter, styles[size])}
            >
                {iconBefore &&
                    <span className={styles.iconBefore}>
                        {iconBefore}
                    </span>
                }
                <input
                    className={className && className}
                    ref={input}
                    id={id}
                    name={name}
                    value={value}
                    type={type}
                    maxLength={maxlength}
                    minLength={minlength}
                    max={max}
                    min={min}
                    pattern={pattern}
                    onChange={handleChange}
                    placeholder={placeholder}
                    onClick={handleInputClick}
                    disabled={disabled}
                    required={required}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onInput={onInput}
                    onKeyDown={handleKeyDown}
                    onWheel={handleWheel}
                    inputMode={getInputMode()}
                    {...dataAttributes}
                />
                {iconAfter &&
                    <span className={classNames(styles.iconAfter)}>
                        {iconAfter}
                    </span>
                }
            </div>
        </>
    );
};

export default InputField;
